import { formatCompanionDateTime } from '@/components/companion/feed/companion-formatters';
import { Button, Flex, Stack, Text } from '@chakra-ui/react';
import { AnswerBody, ReasoningTrace, UserBubble, type TraceStep } from '@fluenceur/agent-blocks';
import type { CompanionPanelTurnDto } from '@fluenceur/types';
import { useTranslation } from 'react-i18next';

/**
 * One question and its answer: the brand's bubble, the trace of the tools the
 * agent actually read, the answer, then how it ended when it did not complete.
 */

/** Past this age an answer shows when it was written: its figures may have moved since. */
const TIMESTAMP_AFTER_MS = 3600_000;

interface CompanionPanelTurnProps {
  turn: CompanionPanelTurnDto;
  onRetry: (question: string) => void;
  /** Another answer is being written: asking again would be refused. */
  isAnswering: boolean;
}

/** Once the turn is over no step is still working, even one the server never closed. */
const traceSteps = (turn: CompanionPanelTurnDto): TraceStep[] =>
  turn.steps.map((step) => ({
    id: step.callId,
    label: step.label,
    status: step.endedAt || turn.status !== 'running' ? 'done' : 'active',
  }));

/** Up to the end of the turn, or of its last step when the turn never completed. */
const durationSeconds = (turn: CompanionPanelTurnDto): number => {
  const stepEnds = turn.steps
    .map((step) => (step.endedAt ? Date.parse(step.endedAt) : NaN))
    .filter(Number.isFinite);
  const end = turn.completedAt
    ? Date.parse(turn.completedAt)
    : stepEnds.length > 0
      ? Math.max(...stepEnds)
      : NaN;
  return Number.isFinite(end)
    ? Math.max(0, Math.round((end - Date.parse(turn.startedAt)) / 1000))
    : 0;
};

const paragraphs = (text: string): string[] =>
  text
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

export const CompanionPanelTurn = ({ turn, onRetry, isAnswering }: CompanionPanelTurnProps) => {
  const { t, i18n } = useTranslation();
  const running = turn.status === 'running';
  const writtenAt = turn.completedAt ?? turn.startedAt;
  const stamp =
    !running && Date.now() - Date.parse(writtenAt) > TIMESTAMP_AFTER_MS
      ? formatCompanionDateTime(writtenAt, i18n.language)
      : null;
  const activeStep = turn.steps.findLast((step) => !step.endedAt) ?? turn.steps.at(-1);
  const canRetry = turn.status === 'interrupted' || turn.status === 'failed';

  return (
    <Stack gap="3" as="article">
      <UserBubble text={turn.question} />
      {/* Every completed answer folds to its duration, even one that read nothing.
          One that stopped short folds to its steps: « Vérifié » would read as a success. */}
      {running || turn.status === 'completed' || turn.steps.length > 0 ? (
        <ReasoningTrace
          status={running ? 'running' : turn.status === 'completed' ? 'done' : 'unfinished'}
          label={activeStep?.label ?? t('companion.conversation.reading')}
          steps={traceSteps(turn)}
          durationSeconds={durationSeconds(turn)}
          sourceCount={turn.sources.length}
        />
      ) : null}
      {turn.answerText ? (
        <AnswerBody isReady={turn.status === 'completed'}>
          {paragraphs(turn.answerText).map((paragraph, index) => (
            <Text as="p" key={index} whiteSpace="pre-wrap">
              {paragraph}
            </Text>
          ))}
        </AnswerBody>
      ) : null}
      {turn.status === 'failed' || turn.status === 'stopped' || turn.status === 'interrupted' ? (
        <Flex align="center" gap="2" wrap="wrap">
          <Text textStyle="agent.meta" color="agent.fgMuted">
            {turn.status === 'failed'
              ? t('companion.conversation.failed')
              : t('companion.conversation.interrupted')}
          </Text>
          {canRetry ? (
            <Button
              variant="ghost"
              size="xs"
              colorPalette="agent"
              disabled={isAnswering}
              onClick={() => onRetry(turn.question)}
            >
              {t('companion.conversation.retry')}
            </Button>
          ) : null}
        </Flex>
      ) : null}
      {stamp ? (
        <Text textStyle="agent.meta" color="agent.fgMuted">
          {stamp}
        </Text>
      ) : null}
    </Stack>
  );
};
